import classNames from 'classnames'
import _ from 'lodash'
import React from 'react'
import { Icon, Intent } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'

import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { HelpBlock } from 'browser/components/atomic-elements/atoms/help-block/help-block'
import { renderEventTimestamp } from '../util'
import { Entity } from 'shared-libs/models/entity'
import apis from 'browser/app/models/apis'
import './_sensitive-file-access-event-item.scss'
import { Formatter } from 'shared-libs/helpers/formatter'
import { MapPin } from 'lucide-react'

interface IAccessEventItemProps extends IBaseProps {
  entity: Entity
  item: any
  isFirst: boolean
  isLast: boolean
}

const NAMESPACE = 'sensitive_file_access_event'

/**
 * Renders the immutable audit log entry in the Activity Feed displaying who accessed a file and why.
 */
export class SensitiveFileAccessEventItem extends React.Component<IAccessEventItemProps, {}> {
  public render() {
    const { className, entity, isFirst, item } = this.props
    const eventData = item.get(NAMESPACE) || {}
    const requester = eventData.requester
    const isDenied = eventData.output === 'denied' || !!eventData.outcomeReason

    const createdAt = eventData.createdAt || {}

    return (
      <div className={classNames('c-timelineEvent', className)} key={entity.uniqueId}>
        <div
          className={classNames('c-timelineEvent-line', className, {
            'c-timelineEvent-line--topCutOff': isFirst,
          })}
        />
        <div className="c-timelineEvent-dot" />
        <div className="c-timelineEvent-contentContainer">
          <div className="c-timelineEvent-body">
            <HelpBlock>
              <span className="b">{requester.displayName}</span>{' '}
              {renderEventTimestamp(item.creationDate)}
              <br />
              {this.renderHeader()}
              {this.renderAccessReason()}
              {this.renderLocation(createdAt)}
              {this.renderAuditBadge(isDenied)}
            </HelpBlock>
          </div>
        </div>
      </div>
    )
  }
  private renderLocation = (createdAt: any) => {
    // Compose a simple one-line address
    const address = Formatter.formatAddress(createdAt, false)
    if (_.isEmpty(createdAt) || _.isEmpty(address)) {
      return null
    }


    return (
      <div className="c-sensitiveEvent-locationBox">
        <MapPin size={16} style={{ marginRight: 4, verticalAlign: 'middle' }} />
        {address}
      </div>
    )
  }

  private renderHeader = () => {
    const { item } = this.props
    const { fieldPath } = item.get('sensitive_file_access_event', {})

    const prettyLabel = this.createReadableLabel((fieldPath ?? '').split('.'))
    return (
      <HelpBlock>
        <span className="b">Viewed</span> {prettyLabel}
      </HelpBlock>
    )
  }

  private createReadableLabel(path) {
    const { entity } = this.props
    const labels = _.map(path, (key, index) => {
      const schemaEntity = entity.resolveSubschemaByValuePath(_.take(path, Number(index) + 1))
      if (!schemaEntity) {
        console.warn(`Invalid path=${path.join('.')}`)
      }
      return _.get(schemaEntity, 'schema.label', '')
    })
    return _.compact(labels).join(' ')
  }

  private renderAccessReason = () => {
    const { reason } = this.props.item?.get('sensitive_file_access_event', {})

    return (
      <div className="c-sensitiveEvent-reasonBox">
        <div className="c-sensitiveEvent-reasonBox__header">
          <div className="c-sensitiveEvent-reasonBox__icon">
            <Icon icon={IconNames.DOCUMENT} />
          </div>
          <div className="b">Access Reason:</div>
        </div>

        <div className="c-sensitiveEvent-reasonBox__quote i">"{reason}"</div>
      </div>
    )
  }

  private renderAuditBadge = (isDenied: boolean) => {
    const { outcomeReason } = this.props.item?.get(NAMESPACE, {}) || {}

    const auditText = isDenied ? _.startCase(outcomeReason) || 'Unknown' : 'Logged & Audited'

    const iconName = isDenied ? IconNames.LOCK : IconNames.EYE_OPEN
    const badgeText = isDenied ? 'Access Blocked' : 'PII Access'

    // Use Material Red 200 from _colors.scss for a light but more visible danger color
    const dangerColor = isDenied ? '#ef9a9a' : undefined // $v-color-red-200
    const badgeClasses = classNames('c-sensitiveEvent-badge', {
      'c-sensitiveEvent-badge--denied': isDenied,
    })

    return (
      <div className="c-sensitiveEvent-auditRow">
        <div className={badgeClasses}>
          <Icon icon={iconName} size={12} style={{ marginRight: 6 }} color={dangerColor} />
          <span style={{ color: dangerColor }}>{badgeText}</span>
        </div>
        <span className="c-sensitiveEvent-auditText">{auditText}</span>
      </div>
    )
  }
}
